import { UseFormReturn } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { GetCustomV1Response, GetPreferenceResponse } from "@/types/api/api-types";
import { CotationFormData } from "@/types/forms/home/cotationFormData.form";

type UseOtherFeesAutoUpdateProps = {
  preference?: GetPreferenceResponse;
  custom?: GetCustomV1Response;
  otherFeesToggle: boolean;
  cotationForm: UseFormReturn<CotationFormData>;
};

function getApplicableOtherFees(
  feesRange: { quantity: number; amount: number }[] = [],
  bottleCount: number,
) {
  if (!feesRange.length) return 0;
  const sorted = [...feesRange].sort((a, b) => a.quantity - b.quantity);
  const found = sorted.find((range) => range.quantity >= bottleCount);
  if (found) return found.amount;
  // If not found, return the highest quantity's amount
  return sorted[sorted.length - 1].amount;
}

export function useOtherFeesAutoUpdate({
  preference,
  custom,
  otherFeesToggle,
  cotationForm,
}: UseOtherFeesAutoUpdateProps) {
  const customsWatch = cotationForm.watch("country");
  const productsWatch = cotationForm.watch("products");

  const prevCustomsRef = useRef<any>();
  const prevProductsRef = useRef<any>();

  const [isInit, setIsInit] = useState(false);

  useEffect(() => {
    const customsChanged = prevCustomsRef.current !== customsWatch;
    const productsChanged = prevProductsRef.current !== JSON.stringify(productsWatch);

    prevCustomsRef.current = customsWatch;
    prevProductsRef.current = JSON.stringify(productsWatch);

    if (!isInit) {
      setIsInit(true);
      return;
    }

    if (!otherFeesToggle) {
      cotationForm.setValue("otherFees", 0);
      return;
    }
    if (!(customsChanged || productsChanged)) return;
    if (!customsWatch || !custom || !preference) return;

    const bottleCount =
      productsWatch
        .filter(
          (product) =>
            (!!product.value || product.value === 0) &&
            !isNaN(product?.value) &&
            product?.bottleQuantity > 0,
        )
        .reduce?.((acc, p) => acc + (p.bottleQuantity || 0), 0) || 0;

    if (bottleCount === 0) {
      cotationForm.setValue("otherFees", 0);
      return;
    }

    let amount = 0;

    if (custom?.label.includes("USA")) {
      amount = getApplicableOtherFees(preference.otherUsaFees?.otherFeesRange, bottleCount);
    } else if (custom?.euTarifs) {
      amount = getApplicableOtherFees(preference.otherEuFees?.otherFeesRange, bottleCount);
    } else {
      amount = getApplicableOtherFees(preference.otherNonEuFees?.otherFeesRange, bottleCount);
    }
    cotationForm.setValue("otherFees", amount || 0);
  }, [
    otherFeesToggle,
    customsWatch,
    custom,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(productsWatch),
    productsWatch,
    preference,
    cotationForm,
    isInit,
    setIsInit,
  ]);
}
